import { AssistantCreationChat } from "@/components/Chat/header/AssistantCreationChat";
import { AssistantAvatar } from "@/components/Chat/header/AssistantSelector";
import { AssistantForm } from "@/components/forms";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ASSISTANT_CREATION_PROMPT } from "@/lib";
import { cn } from "@/lib/utils";
import { formatToolsForAssistant } from "@/lib/utils/ai/toolFormatting";
import { useAssistantStore, useAuthStore } from "@/store";
import { eventBus } from "@/utils/eventBus";
import { motion } from "framer-motion";
import { Edit, Loader2, Save } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// This component is used to edit assistants using a chat interface
const AssistantEditChat = ({ assistant, onAssistantEdited, onCancel }) => {
  const [userQuery, setUserQuery] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [_generationProgress, setGenerationProgress] = useState(0);

  const handleEditAssistant = async () => {
    if (!userQuery.trim()) {
      console.warn("Empty query");
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(10);

    try {
      const { ChatCompletionService } = await import(
        "@/services/openai/chatCompletionService"
      );
      const chatCompletionService = new ChatCompletionService();

      // Configure system prompt for editing assistant - using the comprehensive format from AssistantCreationChat
      chatCompletionService.setSystemPrompt(ASSISTANT_CREATION_PROMPT);

      setGenerationProgress(30);

      // Create user message with context about the current assistant
      const userMessage = `I need to modify an existing assistant based on the following changes: ${userQuery}

CURRENT ASSISTANT DETAILS FOR REFERENCE:
Name: ${assistant?.name || "Unnamed Assistant"}
Description: ${assistant?.description || "No description provided"}
Instructions: ${assistant?.instructions || "No instructions provided"}
Model: ${assistant?.model || "gpt-4.1-mini"}
Tools: ${
        assistant?.tools
          ?.map((tool) =>
            tool?.type === "function" ? tool?.function?.name : tool?.type
          )
          .join(", ") || "No tools"
      }

Please create an updated version of this assistant that incorporates the requested changes while preserving its core functionality unless explicitly instructed to change it. Treat this as creating a new assistant that builds upon the existing one with the specified modifications.`;

      const response = await chatCompletionService.createChatCompletion(
        [{ role: "user", content: userMessage }],
        {
          featureId: "assistant_generation",
        }
      );

      setGenerationProgress(70);

      // Extract the response content and parse the JSON
      const content = response.choices[0].message.content;

      // Parse the structured output - the creation prompt returns clean JSON
      const parsedData = JSON.parse(content.trim());

      setGenerationProgress(100);

      // Pass the edited assistant data to the parent component
      onAssistantEdited({
        name: parsedData.name,
        description: parsedData.description,
        instructions: parsedData.instructions,
        tools: parsedData.recommended_tools,
        reasoning: parsedData.reasoning,
        prompt: userQuery,
        model: parsedData.recommended_model,
        primary_purpose: parsedData.primary_purpose,
      });
    } catch (error) {
      console.error("Error editing assistant:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEditAssistant();
    }
  };

  return (
    <div className="p-2 rounded-md relative">
      {isGenerating ? (
        <div className="min-h-[120px] w-full p-3 border border-purple-500/30 bg-white/10 dark:bg-zinc-800/10 rounded-md">
          <div className="space-y-3">
            <Skeleton className="h-4 w-[90%]" />
            <Skeleton className="h-4 w-[80%]" />
            <Skeleton className="h-4 w-[85%]" />
            <Skeleton className="h-4 w-[75%]" />
            <Skeleton className="h-4 w-[60%]" />
          </div>
        </div>
      ) : (
        <textarea
          placeholder="Describe what changes you'd like to make to this assistant... (e.g., 'Make it focus more on marketing analysis' or 'Add capabilities for financial research')"
          value={userQuery}
          onChange={(e) => setUserQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="min-h-[120px] w-full border-purple-500/30 focus:border-none focus-visible:outline-none bg-white/10 dark:bg-zinc-800/10 resize-none text-sm focus:shadow-[0_0_0_2px_rgba(88,28,135,0.8)] focus:bg-gradient-to-br focus:from-purple-700/10 focus:via-purple-600/8 focus:to-purple-500/6 transition-all duration-200"
          style={{
            "--tw-ring-color": "transparent",
            "--tw-ring-offset-width": "0px",
            "--tw-ring-offset-color": "transparent",
            "--tw-ring-offset-shadow": "none",
            "--tw-ring-shadow": "none",
          }}
        />
      )}

      <div className="absolute bottom-4 right-4 pointer-events-none">
        <Button
          size="icon"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            handleEditAssistant();
          }}
          disabled={isGenerating || !userQuery.trim()}
          className="h-8 w-8 rounded-md bg-transparent border-purple-500 text-purple-500 hover:bg-purple-500/10 hover:text-purple-600 shadow-sm pointer-events-auto"
        >
          {isGenerating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Edit className="h-4 w-4" />
          )}
        </Button>
      </div>

      <button onClick={onCancel} className="sr-only" aria-label="Cancel">
        Cancel
      </button>
    </div>
  );
};

AssistantEditChat.propTypes = {
  assistant: PropTypes.object.isRequired,
  onAssistantEdited: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export const RunSettingsPanel = ({
  open,
  onClose,
  className,
  onShowAuthOverlay,
  hideTrigger = false,
}) => {
  const { currentAssistant, updateAssistant, setCurrentAssistant, assistants } =
    useAssistantStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(open);
  const [ignoreNextClose, setIgnoreNextClose] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [_showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveFunction, setSaveFunction] = useState(null);
  const [isCreationMode, setIsCreationMode] = useState(false);
  const [creationComplete, setCreationComplete] = useState(false);
  // Add state for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  // Add state for creation warning
  const [showCreationWarning, setShowCreationWarning] = useState(false);

  // Sync the internal state with prop when it changes from closed to open
  useEffect(() => {
    if (open) {
      setInternalOpen(true);
      // When opened programmatically, prevent immediate closing
      setIgnoreNextClose(true);

      // Reset the ignore flag after a delay
      const timer = setTimeout(() => {
        setIgnoreNextClose(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open]);

  // Listen for events to enable creation mode
  useEffect(() => {
    const handleCreationMode = (enabled) => {
      setIsCreationMode(enabled);
      setCreationComplete(false);
    };

    eventBus.on("assistant-creation-mode", handleCreationMode);
    return () => {
      eventBus.off("assistant-creation-mode", handleCreationMode);
    };
  }, []);

  // Auto-open panel for new assistants
  useEffect(() => {
    if (currentAssistant?.name === "New Assistant" && !internalOpen) {
      setInternalOpen(true);
      setIgnoreNextClose(true);

      // Reset the ignore flag after a delay
      const timer = setTimeout(() => {
        setIgnoreNextClose(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentAssistant, internalOpen]);

  // Handle closing with a slight delay to prevent accidental closes
  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      // Check if we're in incomplete creation mode
      if (
        isCreationMode &&
        !creationComplete &&
        currentAssistant?.name === "New Assistant" &&
        !currentAssistant?.metadata?.generated_from_prompt
      ) {
        // Show warning instead of closing
        setShowCreationWarning(true);
        return;
      }

      // Check if we should ignore this close event only for very recent programmatic opens
      if (ignoreNextClose) {
        return;
      }

      // Close immediately for user interactions (like outside clicks)
      // Only use setTimeout for programmatic closes to prevent race conditions
      setInternalOpen(false);
      onClose(false);
      // Reset creation and edit modes when panel is closed
      setIsCreationMode(false);
      setCreationComplete(false);
      setIsEditMode(false);
      setShowCreationWarning(false);
    } else {
      setInternalOpen(true);
      onClose(true);
    }
  };

  const handleSavingStateChange = (
    saving,
    success,
    hasPendingChanges,
    saveFn
  ) => {
    setIsSaving(saving);
    setShowSaveSuccess(success);
    setHasUnsavedChanges(hasPendingChanges);
    if (saveFn) {
      setSaveFunction(() => saveFn);
    }
  };

  // Handle toggling edit mode
  const handleToggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // Handle assistant generation from chat
  const handleAssistantGenerated = async (generatedData) => {
    if (!currentAssistant || !currentAssistant.id) {
      console.error("Cannot update assistant: missing assistant data");
      return;
    }

    setIsSaving(true);
    setShowCreationWarning(false); // Clear any warning

    try {
      // Convert tool names to actual tool objects
      const formattedTools = formatToolsForAssistant(generatedData.tools);

      // Update the assistant with generated data
      await updateAssistant(currentAssistant.id, {
        name: generatedData?.name,
        description: generatedData.description,
        instructions: generatedData.instructions,
        tools: formattedTools,
        model: generatedData.model || "gpt-4.1-mini", // Use the recommended model
        metadata: {
          ...currentAssistant.metadata,
          generated_from_prompt: "true",
          generation_prompt: generatedData.prompt,
          recommended_tools: JSON.stringify(generatedData.tools),
          reasoning: generatedData.reasoning,
          primary_purpose: generatedData.primary_purpose || "general",
          updated_at: new Date().toISOString(),
        },
      });

      // Mark creation as complete to show the form
      setCreationComplete(true);
    } catch (error) {
      console.error("Error updating assistant:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle assistant edited through chat
  const handleAssistantEdited = async (editedData) => {
    if (!currentAssistant || !currentAssistant.id) {
      console.error("Cannot update assistant: missing assistant data");
      return;
    }

    setIsSaving(true);
    setShowCreationWarning(false); // Clear any warning

    try {
      // Convert tool names to actual tool objects
      const formattedTools = formatToolsForAssistant(editedData.tools);

      // Update the assistant with the edited data
      await updateAssistant(currentAssistant.id, {
        name: editedData?.name,
        description: editedData.description,
        instructions: editedData.instructions,
        tools: formattedTools,
        model: editedData.model || currentAssistant.model || "gpt-4.1-mini",
        metadata: {
          ...currentAssistant.metadata,
          edited_from_prompt: "true",
          edit_prompt: editedData.prompt,
          recommended_tools: JSON.stringify(editedData.tools),
          reasoning: editedData.reasoning,
          primary_purpose:
            editedData.primary_purpose ||
            currentAssistant.metadata?.primary_purpose ||
            "general",
          updated_at: new Date().toISOString(),
        },
      });

      // Exit edit mode
      setIsEditMode(false);
    } catch (error) {
      console.error("Error updating assistant:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle canceling creation
  const handleCancelCreation = () => {
    setIsCreationMode(false);
    setCreationComplete(false);
    setShowCreationWarning(false);
    setInternalOpen(false);
    onClose(false);

    // Navigate back to a default assistant or handle the incomplete state
    // You might want to delete the incomplete assistant here
    if (
      currentAssistant?.name === "New Assistant" &&
      !currentAssistant?.metadata?.generated_from_prompt
    ) {
      // Reset to a default state or previous assistant

      // Try to find a default assistant or the first available assistant
      const defaultAssistant = assistants?.find(
        (a) => a.is_default === true || a.metadata?.is_default === "true"
      );
      const fallbackAssistant = defaultAssistant || assistants?.[0];

      if (fallbackAssistant && fallbackAssistant.id !== currentAssistant.id) {
        setCurrentAssistant(fallbackAssistant);
      }
    }
  };

  // If user is not authenticated, render just a button without popover
  if (!user) {
    if (hideTrigger) {
      return null; // Don't render anything if trigger is hidden and user is not authenticated
    }
    return (
      <motion.button
        className={cn(
          "flex h-6 w-6 xs:h-7 xs:w-7 sm:h-8 sm:w-8 items-center justify-center",
          "rounded-2xl",
          "bg-transparent",
          "border border-input/50 hover:border-input",
          "hover:bg-transparent",
          "transition-all duration-200",
          "focus:outline-none",
          "focus:ring-2",
          "focus:ring-purple-400/30",
          "flex-shrink-0",
          "focus:border-purple-400/50",
          "relative group",
          "backdrop-blur-[0.5px]",
          className
        )}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          navigate("/auth/login");
        }}
      >
        <div className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none blur-[1px] bg-gradient-to-r from-white/[0.02] via-white/[0.03] to-white/[0.02]" />
        <div className="rounded-full p-0.5 border dark:border-white/60 border-black/60 overflow-hidden">
          <AssistantAvatar
            assistant={currentAssistant}
            className="h-3 w-3 xs:h-4 xs:w-4 sm:h-5 sm:w-5"
          />
        </div>
      </motion.button>
    );
  }

  return (
    <Popover open={internalOpen} onOpenChange={handleOpenChange}>
      {/* Always render trigger for anchor positioning, but hide it visually when hideTrigger is true */}
      <PopoverTrigger asChild>
        <motion.button
          className={cn(
            "flex items-center justify-center",
            "rounded-2xl",
            "bg-transparent",
            "border border-input/50 hover:border-input",
            "hover:bg-transparent",
            "transition-all duration-200",
            "focus:outline-none",
            "focus:ring-2",
            "focus:ring-purple-400/30",
            "flex-shrink-0",
            "focus:border-purple-400/50",
            "relative group",
            "backdrop-blur-[0.5px]",
            hideTrigger
              ? "opacity-0 pointer-events-none w-1 h-1 overflow-hidden"
              : "h-6 w-6 xs:h-7 xs:w-7 sm:h-8 sm:w-8",
            className
          )}
          whileHover={!hideTrigger ? { scale: 1.05 } : {}}
          whileTap={!hideTrigger ? { scale: 0.95 } : {}}
          aria-hidden={hideTrigger}
          tabIndex={hideTrigger ? -1 : 0}
        >
          {!hideTrigger && (
            <>
              <div className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none blur-[1px] bg-gradient-to-r from-white/[0.02] via-white/[0.03] to-white/[0.02]" />
              <div className="rounded-full p-0.5 border dark:border-white/60 border-black/60 overflow-hidden">
                <AssistantAvatar
                  assistant={currentAssistant}
                  className="h-3 w-3 xs:h-4 xs:w-4 sm:h-5 sm:w-5"
                />
              </div>
            </>
          )}
        </motion.button>
      </PopoverTrigger>
      {isCreationMode &&
      !creationComplete &&
      currentAssistant?.name === "New Assistant" &&
      !currentAssistant?.metadata?.generated_from_prompt ? (
        // Creation mode - show only chat input without header
        <PopoverContent
          align="end"
          className="w-[90vw] xs:w-[350px] sm:w-[420px] p-0 bg-white dark:bg-zinc-900 backdrop-blur-sm border-2 border-border/40 rounded-2xl shadow-lg"
          sideOffset={5}
          style={{ zIndex: 400 }}
          forceMount
          onInteractOutside={(e) => {
            // Prevent closing when clicking outside during creation
            e.preventDefault();
            setShowCreationWarning(true);
          }}
        >
          <AssistantCreationChat
            onAssistantGenerated={handleAssistantGenerated}
            onCancel={handleCancelCreation}
            showWarning={showCreationWarning}
            onWarningDismiss={() => setShowCreationWarning(false)}
          />
        </PopoverContent>
      ) : isEditMode ? (
        // Edit mode - show edit chat interface
        <PopoverContent
          align="end"
          className="w-[90vw] xs:w-[350px] sm:w-[420px] p-0 bg-white dark:bg-zinc-900 backdrop-blur-sm border-2 border-border/40 rounded-2xl shadow-lg"
          sideOffset={5}
          style={{ zIndex: 400 }}
          forceMount
          // No onInteractOutside handler - allow normal outside clicks to close edit mode
        >
          <AssistantEditChat
            assistant={currentAssistant}
            onAssistantEdited={handleAssistantEdited}
            onCancel={() => setIsEditMode(false)}
          />
        </PopoverContent>
      ) : !user ? (
        // Login prompt for non-authenticated users
        <PopoverContent
          align="end"
          className="w-[200px] p-3 z-[400] rounded-lg bg-white dark:bg-zinc-900 backdrop-blur-sm border-2 border-border/40"
          sideOffset={5}
          style={{ zIndex: 400 }}
          forceMount
        >
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Sign in to customize your assistant
            </p>
            <Button
              onClick={() =>
                onShowAuthOverlay && onShowAuthOverlay("Assistant Settings")
              }
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg"
            >
              Sign In
            </Button>
          </div>
        </PopoverContent>
      ) : currentAssistant?.name === "New Assistant" &&
        !currentAssistant?.metadata?.generated_from_prompt ? (
        // Incomplete assistant - show message to complete creation
        <PopoverContent
          align="end"
          className="w-[90vw] xs:w-[350px] sm:w-[420px] p-4 bg-white dark:bg-zinc-900 backdrop-blur-sm border-2 border-border/40 rounded-2xl shadow-lg"
          sideOffset={5}
          style={{ zIndex: 400 }}
          forceMount
        >
          <div className="text-center space-y-3">
            <div className="h-12 w-12 mx-auto bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
              <div className="h-6 w-6 bg-purple-500 rounded-full animate-pulse"></div>
            </div>
            <h3 className="text-lg font-medium text-foreground">
              Complete Assistant Creation
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              This assistant needs to be configured before you can access its
              settings. Please complete the creation process first.
            </p>
            <Button
              onClick={() => {
                setIsCreationMode(true);
                setCreationComplete(false);
              }}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              Continue Creation
            </Button>
          </div>
        </PopoverContent>
      ) : (
        // Regular settings mode - show header and form
        <PopoverContent
          align="end"
          className="w-[90vw] xs:w-[350px] sm:w-[420px] p-0 bg-white dark:bg-zinc-900 backdrop-blur-sm border-2 border-border/40 shadow-md max-h-[75vh] overflow-hidden z-[400] rounded-2xl"
          sideOffset={5}
          style={{ zIndex: 400 }}
          forceMount
          // No onInteractOutside handler - allow normal outside clicks to close the assistant form
        >
          <div className="px-3 py-3 border-b border-gray-800 flex items-center justify-between">
            <h3 className="text-[15px] font-medium text-foreground">
              Assistant Settings
            </h3>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 flex items-center gap-1 border-blue-500 text-blue-500 hover:bg-blue-500/10 bg-transparent rounded-2xl text-[11px]"
                onClick={handleToggleEditMode}
              >
                <Edit className="h-3 w-3" />
                <span>Edit</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 flex items-center gap-1 border-purple-500 text-purple-500 hover:bg-purple-500/10 bg-transparent rounded-2xl text-[11px]"
                onClick={() => saveFunction && saveFunction()}
                disabled={isSaving || !hasUnsavedChanges || !saveFunction}
              >
                {isSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
                <span>Save</span>
              </Button>
            </div>
          </div>
          <div className="max-h-[calc(75vh-60px)] overflow-auto">
            <div className="p-3 pb-2">
              {currentAssistant && (
                <AssistantForm
                  assistant={currentAssistant}
                  formState={{}}
                  onSavingStateChange={handleSavingStateChange}
                />
              )}
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

RunSettingsPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  className: PropTypes.string,
  onShowAuthOverlay: PropTypes.func,
  hideTrigger: PropTypes.bool,
};

export default RunSettingsPanel;
